import React, { useContext, useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Button from '@components/Shared/Buttons/Button';
import { DXAccountContext } from '@contexts/DXAccountContext';
import { getUTMParameters } from 'pubweb-smokey/dist/utils/utils';
import { setLocalStorage } from '@utils/utils';
import { getSiteUrl } from '@utils/config';
import PlannerBadges from './images/PlannerBadges.svg';
import { PlannerStyled } from './Planner.styled';
import { pushGtmFormAction } from '@utils/gtmUtils';

const plannerStaticData = {
  heading: 'MyHome Planner',
  subheading: 'Get ready for homeownership with your MyHome Account.',
  infoBlurb1:
    'You can explore the buying process, save your favorite features, see recommended homes and track your progress. Then, you can connect with a Clayton® home expert and tour homes nearby at your local retailer.',
};

const Planner = () => {
  const accountContext = useContext(DXAccountContext);
  const plannerImageHeroBackgrounds = {
    smallMobile: '/public-assets/images/planner/PlannerHeroMobile.png',
    largeMobile: '/public-assets/images/planner/PlannerHeroLgMobile.png',
    tablet: '/public-assets/images/planner/PlannerHeroTablet.png',
    desktop: '/public-assets/images/planner/PlannerHeroDesktop.png',
  };
  const redirectPath = accountContext.state.postLoginRedirectPath
    ? accountContext.state.postLoginRedirectPath
    : '/portal/';
  const router = useRouter();

  //Storing UTM Params into Local Storage
  useEffect(() => {
    setLocalStorage('utm_params', getUTMParameters());
  }, []);

  // Put the button text here because DM would like to use button text for form_submit_text field.
  // Thought this would be easier to keep form_submit_text and button text in sync in case these ever change.
  const loginButtonText = 'Log In';
  const signUpButtonText = 'Sign Up';

  // Push a custom GTM form action event for login or sign up.
  const pushFormEvent = (requestType) => {
    pushGtmFormAction({
      form_action_type: 'submit',
      form_name: 'myhome_account',
      form_method: 'auth_0',
      form_id:
        requestType === 'signup'
          ? 'myhome_planner_sign_up'
          : 'myhome_planner_login',
      form_submit_text:
        requestType === 'signup'
          ? signUpButtonText.trim().toLowerCase().replaceAll(' ', '_')
          : loginButtonText.trim().toLowerCase().replaceAll(' ', '_'),
    });
  };

  return (
    <PlannerStyled imageBackground={plannerImageHeroBackgrounds}>
      <Head>
        <title>Planner Home Buying Planner | Clayton Homes</title>
        <meta
          name="description"
          content=" Shop homes in your Planner Planner to save your budget, land preference and dream home information in your Clayton Planner Account."
        />
        <link rel="canonical" href={`${getSiteUrl(router.pathname)}`} />
        <meta
          property="og:image"
          content={getSiteUrl('/public-assets/images/clayton-logo.jpg')}
        />
      </Head>

      <div className="constrained-width">
        <div className="planner-landing-content">
          <h1 className="planner-landing-header">
            {plannerStaticData.heading}
          </h1>
          <h3 className="planner-landing-subheader">
            {plannerStaticData.subheading}
          </h3>
          <hr className="planner-landing-linebreak solid" />
          <div className="planner-landing-info">
            <p className="planner-info-blurb">{plannerStaticData.infoBlurb1}</p>
          </div>
          <div className="planner-landing-cta">
            <Button
              onButtonClick={() => {
                pushFormEvent('login');
                accountContext.actions.accountLogin().then(() => {
                  accountContext.actions.navigateToPortal(redirectPath);
                });
              }}
              className="gtm-planner-account-login"
            >
              {loginButtonText}
            </Button>
            <Button
              buttonStyle="outlined"
              onButtonClick={() => {
                pushFormEvent('signup');
                accountContext.actions.accountSignup().then(() => {
                  accountContext.actions.navigateToPortal(redirectPath);
                });
              }}
              className="gtm-planner-account-sign-up"
            >
              {signUpButtonText}
            </Button>
          </div>
        </div>
        <div className="planner-landing-hero-image">
          <div className="planner-landing-badges">
            <PlannerBadges viewBox="0 0 429 604" />
          </div>
        </div>
      </div>
      <div className="planner-landing-hero-background" />
    </PlannerStyled>
  );
};

export default Planner;
